/**
 * Single Row Template Calculations
 *
 * Template-specific calculation utilities for single-row hangars
 */

import type { SingleRowParams } from './params'
import {
  calculateUnitWidthsWithOverrides,
  redistributeUnitWidths,
  type WidthCalculationResult,
} from '../base'

/**
 * Extended result with suggested fixes
 */
export interface SingleRowCalculationResult extends WidthCalculationResult {
  /** Actions user can take to balance layout */
  suggestedFixes: SuggestedFix[]
  /** Minimum viable unit width given current config */
  minViableUnitWidth: number
}

/**
 * Suggested fix for layout imbalance
 */
export interface SuggestedFix {
  type: 'expand_building' | 'redistribute' | 'reduce_count' | 'reduce_unit'
  label: string
  description: string
  newValue?: number
}

/**
 * Calculate unit layout for single-row template
 */
export function calculateSingleRowLayout(params: SingleRowParams): SingleRowCalculationResult {
  // Get base calculation
  const result = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.unit_count,
    params.wall_thickness_ft,
    params.unit_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Calculate minimum viable unit width
  const minViableUnitWidth = Math.min(...result.widths)

  // Generate suggested fixes if not balanced
  const suggestedFixes: SuggestedFix[] = []

  if (!result.isBalanced) {
    if (result.difference_ft > 0) {
      // Over: units + walls exceed building length
      suggestedFixes.push({
        type: 'expand_building',
        label: `Expand to ${result.suggestedBuildingLength.toFixed(0)} ft`,
        description: `Increase building length to fit all units`,
        newValue: result.suggestedBuildingLength,
      })

      if (params.unit_count > 1) {
        suggestedFixes.push({
          type: 'redistribute',
          label: 'Redistribute equally',
          description: 'Reset all units to equal width',
        })
      }
    } else {
      // Under: building has extra space
      suggestedFixes.push({
        type: 'redistribute',
        label: 'Fill space equally',
        description: 'Expand units to use available space',
      })

      if (params.unit_count < 20) {
        const additionalUnits = Math.floor(Math.abs(result.difference_ft) / 30) // ~30ft per unit
        if (additionalUnits > 0) {
          suggestedFixes.push({
            type: 'reduce_count', // Actually increase in this case
            label: `Add ${additionalUnits} unit(s)`,
            description: `Use extra space for more units`,
            newValue: params.unit_count + additionalUnits,
          })
        }
      }
    }
  }

  // Check for units that are too narrow
  const MIN_UNIT_WIDTH = 15
  const tooNarrowCount = result.widths.filter(w => w < MIN_UNIT_WIDTH).length
  if (tooNarrowCount > 0) {
    suggestedFixes.push({
      type: 'reduce_count',
      label: `Reduce to ${params.unit_count - tooNarrowCount} units`,
      description: `${tooNarrowCount} unit(s) are too narrow (< ${MIN_UNIT_WIDTH} ft)`,
      newValue: params.unit_count - tooNarrowCount,
    })
  }

  return {
    ...result,
    suggestedFixes,
    minViableUnitWidth,
  }
}

/**
 * Get redistributed widths that fit exactly in building
 */
export function getRedistributedWidths(params: SingleRowParams): number[] {
  return redistributeUnitWidths(
    params.width_ft,
    params.unit_count,
    params.wall_thickness_ft,
    params.unit_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )
}

/**
 * Calculate optimal unit count for given building length
 *
 * @param buildingLength - Total building length
 * @param wallThickness - Wall thickness
 * @param targetUnitWidth - Desired unit width (default 40)
 * @returns Recommended unit count
 */
export function calculateOptimalUnitCount(
  buildingLength: number,
  wallThickness: number,
  targetUnitWidth: number = 40
): number {
  // Building length = (unit_count + 1) * wall_thickness + unit_count * unit_width
  // unit_count = (building_length - wall_thickness) / (unit_width + wall_thickness)
  const count = (buildingLength - wallThickness) / (targetUnitWidth + wallThickness)
  return Math.max(1, Math.min(20, Math.round(count)))
}

/**
 * Calculate suggested building length for unit count
 *
 * @param unitCount - Desired number of units
 * @param unitWidth - Width per unit
 * @param wallThickness - Wall thickness
 * @returns Suggested building length
 */
export function calculateSuggestedBuildingLength(
  unitCount: number,
  unitWidth: number,
  wallThickness: number
): number {
  return unitCount * unitWidth + (unitCount + 1) * wallThickness
}
